import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

import AlertIcon from 'pages/shared/Alert/Icon'
import LegendGroup from 'pages/Device/ThermostatRthPanel/Legend/LegendGroup'
import { DeviceConnectionState } from 'types/Alert'

type AlertsLegendItem = {
  label: string
  seriesName: string
  emptyDataSet?: boolean
  icon?: JSX.Element
}

export const ALERTS_LEGEND_ITEMS: ReadonlyArray<AlertsLegendItem> = Object.freeze([
  Object.freeze({ label: 'Critical Alerts', seriesName: 'critical' }),
  Object.freeze({ label: 'Major Alerts', seriesName: 'major' }),
  Object.freeze({ label: 'Minor Alerts', seriesName: 'minor' }),
  Object.freeze({ label: 'Major SDAs', seriesName: 'major-sda' }),
  Object.freeze({ label: 'Minor SDAs', seriesName: 'minor-sda' })
])

export const DEVICE_CONNECTION_STATUS_ALERTS_LEGEND_ITEMS: ReadonlyArray<AlertsLegendItem> = Object.freeze([
  Object.freeze({ label: 'Connected', seriesName: 'connected' }),
  Object.freeze({ label: 'Disconnected', seriesName: 'disconnected' }),
  Object.freeze({ label: 'Restarted', seriesName: 'restarted' })
])

type Alert = {
  severity: string
}

type Props = {
  alerts: Array<Alert>
  deviceConnectionStatusAlerts: Array<DeviceConnectionState>
  isServerlessRth: boolean
  selectedLegends: Array<string>
  onClickLegendItem: (i) => void
  onClickAllOrNone: (i, n) => void
}

const AlertsLegendGroup = ({ alerts, deviceConnectionStatusAlerts, isServerlessRth, selectedLegends, onClickLegendItem, onClickAllOrNone }: Props) => {
  const [parsedAlertLegendItems, setParsedAlertLegendItems] = useState<ReadonlyArray<AlertsLegendItem>>([])
  const countSeverityOccurrences = () => {
    const severityCounts = {}

    alerts?.forEach((alert) => {
      if (severityCounts[alert.severity]) {
        severityCounts[alert.severity]++
      } else {
        severityCounts[alert.severity] = 1
      }
    })

    return severityCounts
  }

  const countDeviceConnectionStatusOccurrences = () => {
    const statusCounts = {}

    deviceConnectionStatusAlerts?.forEach((alert) => {
      if (statusCounts[alert.state]) {
        statusCounts[alert.state]++
      } else {
        statusCounts[alert.state] = 1
      }
    })

    return statusCounts
  }

  useEffect(() => {
    const newAlertLegendItems = structuredClone(ALERTS_LEGEND_ITEMS)
    newAlertLegendItems.forEach((legendItem) => {
      legendItem.emptyDataSet = true
      legendItem.icon = <S.AlertIcon severity={legendItem.seriesName} />
    })

    const severityCounts = countSeverityOccurrences()
    const deviceConnectionStatusCounts = countDeviceConnectionStatusOccurrences()

    Object.keys(severityCounts).forEach((severity) => {
      const legendItem = newAlertLegendItems.find((item) => item.seriesName === severity)

      if (legendItem) {
        legendItem.label = legendItem.label.split('(')[0].trim()

        legendItem.label = `${legendItem.label} (${severityCounts[severity]})`
        legendItem.emptyDataSet = false
      }
    })

    if (isServerlessRth) {
      const deviceConnectionStatusLegendItems = structuredClone(DEVICE_CONNECTION_STATUS_ALERTS_LEGEND_ITEMS)

      deviceConnectionStatusLegendItems.forEach((legendItem) => {
        legendItem.emptyDataSet = true
        legendItem.icon = <S.DeviceConnectionStatusIcon src={'/img/alert-rth-spirte-icon/device_status_info.svg'} />
      })
      Object.keys(deviceConnectionStatusCounts).forEach((status) => {
        const legendItem = deviceConnectionStatusLegendItems.find((item) => item.seriesName === status)

        if (legendItem) {
          legendItem.label = `${legendItem.label} (${deviceConnectionStatusCounts[status]})`
          legendItem.emptyDataSet = false
        }
      })

      setParsedAlertLegendItems([
        ...newAlertLegendItems,
        ...deviceConnectionStatusLegendItems
      ])
    } else {
      setParsedAlertLegendItems(newAlertLegendItems)
    }
  }, [alerts]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LegendGroup
      noDataText="No Alerts"
      label="Alerts"
      legendItems={parsedAlertLegendItems}
      selectedLegends={selectedLegends}
      onLegendItemClickCallback={onClickLegendItem}
      onLegendItemSelectAllClickCallback={onClickAllOrNone}
    />
  )
}

export default AlertsLegendGroup

const S = {
  AlertIcon: styled(AlertIcon)`
    margin-right: 5px;
  `,
  DeviceConnectionStatusIcon: styled.img`
    width: 20px;
    height: 20px;
    margin-right: 5px;
    margin-bottom: -4px;
  `
}
